import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Event as EventIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocationOn as LocationIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';

const EnhancedDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const isAdmin = user?.role === 'SYSTEM_ADMIN';

  useEffect(() => {
    if (isAdmin) {
    fetchDashboardData();
  }else{
    setLoading(false);
  }
  }, [isAdmin]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data
      const [statsRes, usersRes, eventsRes] = await Promise.all([
        axios.get('/dashboard/stats'),
        axios.get('/dashboard/recent-users?limit=3'),
        axios.get('/dashboard/upcoming-events?limit=2')
      ]);

      setStats(statsRes.data.data);
      setRecentUsers(usersRes.data.data);
      setUpcomingEvents(eventsRes.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, percentage }) => (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box
            sx={{
              backgroundColor: `${color}15`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ color, fontSize: 28 }} />
          </Box>
          {percentage !== undefined && (
            <Box display="flex" alignItems="center">
              {percentage >= 0 ? (
                <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20, mr: 0.5 }} />
              ) : (
                <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20, mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                sx={{ color: percentage >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
              >
                {Math.abs(percentage).toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Typography color="text.secondary" variant="body2" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" component="h2" fontWeight={600}>
          {value}
        </Typography>
        {percentage !== undefined && (
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            from last month
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Verified':
        return 'success';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!isAdmin && !loading) {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
      <Typography variant="h6" color="error">
        You are logged in, but not authorized to view the admin dashboard.
      </Typography>
    </Box>
  );
}

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Welcome Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Welcome, Admin!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with LaBaKa today
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats?.users?.total?.toLocaleString() || '0'}
            icon={PeopleIcon}
            color="#5B93FF"
            percentage={stats?.users?.percentageChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue"
            value={`$${(stats?.revenue?.amount / 1000).toFixed(0)}k`}
            icon={MoneyIcon}
            color="#00D4AA"
            percentage={stats?.revenue?.percentageChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Events"
            value={stats?.events?.total || '0'}
            icon={EventIcon}
            color="#8B5CF6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending KYC"
            value={stats?.pendingKYC || '0'}
            icon={VerifiedIcon}
            color="#F59E0B"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Users */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Recent Users
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List sx={{ p: 0 }}>
              {recentUsers.map((user, index) => (
                <React.Fragment key={user._id}>
                  <ListItem
                    sx={{
                      px: 0,
                      py: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          backgroundColor: 'primary.main',
                          width: 45,
                          height: 45,
                          fontSize: 16,
                          fontWeight: 600
                        }}
                      >
                        {getInitials(user.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={500}>
                          {user.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      }
                    />
                    <Chip
                      label={user.verificationStatus}
                      size="small"
                      color={getStatusColor(user.verificationStatus)}
                      sx={{ fontWeight: 500 }}
                    />
                  </ListItem>
                  {index < recentUsers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Upcoming Events */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Upcoming Events
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List sx={{ p: 0 }}>
              {upcomingEvents.map((event, index) => (
                <React.Fragment key={event._id}>
                  <ListItem
                    sx={{
                      px: 0,
                      py: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderRadius: 1
                      }
                    }}
                  >
                    <Box flex={1}>
                      <Typography variant="body1" fontWeight={500} gutterBottom>
                        {event.title}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.location}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <GroupsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.registrations}/{event.capacity}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="primary.main" fontWeight={500}>
                        {new Date(event.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                  </ListItem>
                  {index < upcomingEvents.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            {upcomingEvents.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="text.secondary">
                  No upcoming events
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnhancedDashboard;